import React, { useEffect, useRef, useState } from 'react'
import { Button, ConfigProvider, Divider, Flex, Modal, Radio, RadioChangeEvent } from 'antd'
import { AssetType, DigitalAssetDocumentType, Status, UploadCompanyType } from '../../../../types/entities'
import { useAction } from '@agro-club/frontend-shared'
import DigitalAssetActions from '../../../../modules/domain/digitalAsset/duck'
import CSS from 'csstype'
import DigitalAssetTable from './DigitalAssetTable'
import DASearchInputFilter from '../../../components/DigitalAssets/Filters/DASearchInputFilter'
import DAFileTypeFilterSelect from '../../../components/DigitalAssets/Filters/DAFileTypeFilterSelect'
import DAFileSizeFilterSelect from '../../../components/DigitalAssets/Filters/DAFileSizeFilterSelect'
import DACompanyFilterSelect from '../../../components/DigitalAssets/Filters/DACompanyFilterSelect'
import DACreationDateFilterDatePicker from '../../../components/DigitalAssets/Filters/DACreationDateFilterDatePicker'
import { useSelector } from 'react-redux'
import DigitalAssetSelectors from '../../../../modules/domain/digitalAsset/selectors'
import { UploadOutlined, WarningOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import DigitalAssetDocumentForm from '../DigitalAssetDocumentForm'
import DigitalAssetLinkForm from '../DigitalAssetDetailsForm/DigitalAssetLinkForm'
import { AddLinkFormFields, FormFields } from '../DigitalAssetDetailsForm/types'
import AuthSelectors from '../../../../modules/domain/auth/selectors'
import { DigitalAsset } from '../../../../modules/domain/digitalAsset/types'
import DropdownContainer from '../../../components/Generics/DropdownContainer'
import { useTableHeight } from '../../../../hooks/useTableHeight'

const assetActionStyles: CSS.Properties = {
  paddingRight: '1.5em',
}

const filterActionBarStyles: CSS.Properties = {
  paddingLeft: '1.5em',
}

const filterComponentsContainerStyles: CSS.Properties = {
  paddingTop: '1em',
  paddingBottom: '1em',
  paddingRight: '1.5em',
}

enum FormType {
  File,
  Link,
}

const getDocumentTypeFromFile = (fileName: string): DigitalAssetDocumentType => {
  const ext = fileName.split('.').pop()?.toLowerCase()
  switch (ext) {
    case 'pdf':
      return DigitalAssetDocumentType.PDF
    case 'doc':
      return DigitalAssetDocumentType.DOC
    case 'docx':
      return DigitalAssetDocumentType.DOCX
    case 'xls':
      return DigitalAssetDocumentType.XLS
    case 'xlsx':
      return DigitalAssetDocumentType.XLSX
    case 'csv':
      return DigitalAssetDocumentType.CSV
    case 'ppt':
      return DigitalAssetDocumentType.PPT
    case 'pptx':
      return DigitalAssetDocumentType.PPTX
    case 'txt':
      return DigitalAssetDocumentType.TXT
    case 'html':
      return DigitalAssetDocumentType.HTML
    case 'xml':
      return DigitalAssetDocumentType.XML
    case 'png':
      return DigitalAssetDocumentType.PNG
    case 'gif':
      return DigitalAssetDocumentType.GIF
    case 'jpeg':
      return DigitalAssetDocumentType.JPEG
    case 'jpg':
      return DigitalAssetDocumentType.JPG
    case 'tif':
      return DigitalAssetDocumentType.TIF
    case 'tiff':
      return DigitalAssetDocumentType.TIFF
    case 'bmp':
      return DigitalAssetDocumentType.BMP
    case 'webp':
      return DigitalAssetDocumentType.WEBP
    case 'svg':
      return DigitalAssetDocumentType.SVG
    default:
      return DigitalAssetDocumentType.TXT
  }
}

type DigitalAssetTableContentProps = {
  showAssetDetails: (asset: DigitalAsset) => void
  userCompanyType: UploadCompanyType
  canEditOrDelete: boolean
  showUpload: boolean
}

const DigitalAssetTableContent: React.FC<DigitalAssetTableContentProps> = ({
  showAssetDetails,
  userCompanyType,
  canEditOrDelete,
  showUpload,
}) => {
  const { t } = useTranslation('digitalAsset')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [formType, setFormType] = useState<FormType>(FormType.File)
  const [modal, contextHolder] = Modal.useModal()
  const profile = useSelector(AuthSelectors.profile)
  const softDeleteAction = useAction(DigitalAssetActions.softDeleteRequested)
  const documentAssetAddAction = useAction(DigitalAssetActions.addRequested)
  const linkAssetAddAction = useAction(DigitalAssetActions.addLinkRequested)
  const listAction = useAction(DigitalAssetActions.listRequested)

  //scroll table variables
  const contentRef = useRef<HTMLDivElement>(null)
  //todo: this value has to be dynamically calculated to make the table 100% responsive
  const tableAdditionalContentSize = 265
  const tableHeight = useTableHeight(contentRef, tableAdditionalContentSize)

  //download variables
  const downloadDigitalAssetFileAction = useAction(DigitalAssetActions.downloadFileRequested)
  const selectedDigitalAssetDraftAction = useAction(DigitalAssetActions.selectedFileDraftRequested)
  const draftDigitalAssets = useSelector(DigitalAssetSelectors.drafts)
  const [downloadDisabled, setDownloadDisabled] = useState<boolean>(true)

  const handleDownloadFile = async (id: string, documentName?: string) => {
    downloadDigitalAssetFileAction([id, documentName])
  }

  const downloadFiles = () => {
    const fileDigitalAssets = draftDigitalAssets.filter((asset) => asset.asset_type === AssetType.Document)
    fileDigitalAssets.map((asset) => handleDownloadFile(asset.id))
  }

  const handleSoftDelete = () => {
    if (canEditOrDelete) {
      const confirmContent = draftDigitalAssets.map((asset) => {
        return <p key={asset.id}>{asset.name}</p>
      })
      modal.confirm({
        title: t('messages.deleteMessage'),
        content: confirmContent,
        icon: <WarningOutlined />,
        cancelText: t('button.noConfirm'),
        okText: t('button.yesConfirm'),
        onOk: () => {
          const ids = draftDigitalAssets.map((asset) => asset.id)
          softDeleteAction(ids)
          listAction({ page: 1 })
          selectedDigitalAssetDraftAction([])
        },
      })
    }
  }

  //handle download button disable state
  useEffect(() => {
    const isThereSelectedDigitalAssets = draftDigitalAssets && draftDigitalAssets.length > 0
    const isThereDocumentType = draftDigitalAssets.some(
      (digitalAsset) => digitalAsset.asset_type === AssetType.Document,
    )
    const isDisabled = isThereSelectedDigitalAssets && isThereDocumentType
    setDownloadDisabled(!isDisabled)
  }, [draftDigitalAssets])

  const showModal = () => {
    setIsModalOpen(true)
  }

  const onChangeForm = (e: RadioChangeEvent) => {
    setFormType(e.target.value)
  }

  const handleCancel = () => {
    setIsModalOpen(false)
    setFormType(FormType.File)
  }

  const onSubmitLink = async (fields: AddLinkFormFields) => {
    const company_id = profile?.company_id || undefined
    const user_id = profile?.id || undefined
    const args = {
      link_url: fields.link_url,
      asset_name: fields.asset_name,
      description: fields.description,
      create_company_id: company_id,
      create_user_id: user_id,
      status: Status.Active,
      asset_type: AssetType.Link,
      uploaded_by_company_type: userCompanyType,
    }
    linkAssetAddAction(args)
    setIsModalOpen(false)
    listAction({ page: 1 })
  }

  const onSubmitDocument = (fields: FormFields) => {
    const company_id = profile?.company_id || undefined
    const user_id = profile?.id || undefined
    const args = {
      document_type: getDocumentTypeFromFile(fields.file.name),
      document_name: fields.file.name,
      asset_name: fields.asset_name,
      description: fields.description,
      create_company_id: company_id,
      create_user_id: user_id,
      status: Status.Active,
      asset_type: AssetType.Document,
      uploaded_by_company_type: userCompanyType,
    }
    documentAssetAddAction(args, fields.file)
    setIsModalOpen(false)
    listAction({ page: 1 })
  }

  return (
    <>
      {contextHolder}
      <Flex vertical gap="middle" ref={contentRef} flex={1}>
        <Flex vertical style={filterActionBarStyles}>
          <Flex justify="space-between" align="center" flex={1}>
            <Flex gap="middle" flex={1}>
              <DASearchInputFilter />
            </Flex>
            <Flex justify="flex-end" gap="middle" style={assetActionStyles} flex={1}>
              {canEditOrDelete && (
                <Button
                  size="large"
                  color="danger"
                  variant="outlined"
                  onClick={handleSoftDelete}
                  disabled={!draftDigitalAssets || draftDigitalAssets.length === 0}
                >
                  {t('button.delete')}
                </Button>
              )}
              <Button
                size="large"
                color="primary"
                variant="outlined"
                onClick={downloadFiles}
                disabled={downloadDisabled}
              >
                {t('button.download')}
              </Button>
              {showUpload && (
                <Button size="large" type="primary" iconPosition="start" icon={<UploadOutlined />} onClick={showModal}>
                  {t('button.upload')}
                </Button>
              )}
            </Flex>
          </Flex>
          <DropdownContainer isShow={true}>
            <Flex style={filterComponentsContainerStyles} justify={'space-around'} wrap flex={1} gap="middle">
              <DAFileTypeFilterSelect />
              <DAFileSizeFilterSelect />
              <DACompanyFilterSelect />
              <DACreationDateFilterDatePicker />
            </Flex>
          </DropdownContainer>
        </Flex>
        <DigitalAssetTable
          height={tableHeight}
          setDigitalAssetDetail={showAssetDetails}
          canEditOrDelete={canEditOrDelete}
        />
      </Flex>

      {/*upload assets modal*/}
      {/*todo: Probably this as a component*/}
      <Modal open={isModalOpen} title={t('form.add')} footer={null} destroyOnHidden onCancel={handleCancel}>
        <Divider size="middle" />
        <Flex vertical gap="small">
          <Radio.Group value={formType} defaultValue={FormType.File} buttonStyle="solid" block onChange={onChangeForm}>
            <Radio.Button value={FormType.File}>{t('form.uploadFile')}</Radio.Button>
            <Radio.Button value={FormType.Link}>{t('form.addLink')}</Radio.Button>
          </Radio.Group>
          <ConfigProvider
            theme={{
              components: {
                Form: {
                  labelFontSize: 12,
                  verticalLabelPadding: '0 0 2px',
                  itemMarginBottom: 16,
                },
              },
            }}
          >
            {formType === FormType.File && (
              <DigitalAssetDocumentForm cancelCallback={handleCancel} submitCallback={onSubmitDocument} />
            )}
            {formType === FormType.Link && (
              <DigitalAssetLinkForm cancelCallBack={handleCancel} submitCallBack={onSubmitLink} />
            )}
          </ConfigProvider>
        </Flex>
      </Modal>
    </>
  )
}

export default DigitalAssetTableContent
